import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trophy, Users, Edit, User, Award } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import teamImageService from '../services/teamImageService';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';
import { btn } from '../../utils/buttonStyles';

/**
 * TeamDetail - Reusable Team Detail Display Component
 *
 * Props:
 * - team: Team object with all team data (required)
 * - showEditButton: Whether to show edit button (default: true)
 * - onEdit: Callback when edit button is clicked (optional)
 * - onPlayerClick: Callback when player is clicked (optional)
 */
const TeamDetail = ({
  team,
  showEditButton = true,
  onEdit,
  onPlayerClick
}) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();
  const [logoUrl, setLogoUrl] = useState(null);

  // Load team logo URL — new /images/ format used directly; legacy bare filename needs presigned URL
  useEffect(() => {
    if (!team) return;
    const logo = team.team_logo || team.teamLogo;
    if (!logo) return;
    if (logo.startsWith('/images/')) {
      setLogoUrl(logo);
    } else if (logo.includes('.')) {
      teamImageService.getTeamLogoUrl(team.id, logo)
        .then(url => { if (url) setLogoUrl(url); })
        .catch(err => console.error('Failed to load team logo:', err));
    }
  }, [team]);

  if (!team) {
    return (
      <div className="text-center py-12">
        <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className={theme.textSecondary}>No team data available</p>
      </div>
    );
  }

  const teamName = team.team_name || team.teamName;
  const teamLogo = team.team_logo || team.teamLogo;
  const isNewFormat = teamLogo?.startsWith('/images/');
  const isLegacyFile = !isNewFormat && teamLogo && teamLogo.includes('.');
  const maxMembers = team.max_members || team.maxMembers || 0;
  const players = team.players || team.members || [];

  const isOwner = () => {
    if (!user || !team) return false;
    return (
      team.createdBy === user.id ||
      team.ownerId === user.id ||
      team.captainUserId === user.id ||
      team.captain_user_id === user.id
    );
  };

  const handlePlayerClick = (player) => {
    if (onPlayerClick) {
      onPlayerClick(player);
    } else {
      const userHandle = player?.userHandle || player?.user?.userHandle;
      const userId = player?.userId || player?.user?.id || player?.id;
      if (userHandle || userId) {
        navigate(`/player/${userHandle || userId}`);
      }
    }
  };

  const handleEditClick = () => {
    if (onEdit) {
      onEdit(team);
    }
  };

  // Render the team logo
  const renderLogo = () => {
    if (isNewFormat) {
      return <img src={resolveImageUrl(teamLogo)} alt={teamName} className="w-full h-full object-cover rounded-xl" />;
    } else if (isLegacyFile && logoUrl) {
      return <img src={resolveImageUrl(logoUrl)} alt={teamName} className="w-full h-full object-cover rounded-xl" />;
    } else if (teamLogo && !isLegacyFile) {
      return <span className="text-2xl">{teamLogo}</span>;
    } else {
      return teamName?.charAt(0)?.toUpperCase() || 'T';
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <div className="flex items-center gap-x-4">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
              {renderLogo()}
            </div>
            <div>
              <h1 className={`text-3xl font-bold ${theme.text}`}>{teamName}</h1>
              {team.ownerInfo && (
                <p className={`${theme.textSecondary} flex items-center mt-1 text-sm`}>
                  {team.ownerInfo.type === 'group' ? (
                    <>
                      <Users className="w-4 h-4 mr-1 flex-shrink-0" />
                      <Link
                        to={`/squads/${team.ownerInfo.id}`}
                        className="text-blue-500 hover:text-blue-600 hover:underline font-medium"
                      >
                        {team.ownerInfo.name}
                      </Link>
                    </>
                  ) : (
                    <>
                      <User className="w-4 h-4 mr-1 flex-shrink-0" />
                      <Link
                        to={`/player/${team.ownerInfo.id}`}
                        className="text-blue-500 hover:text-blue-600 hover:underline font-medium"
                      >
                        {team.ownerInfo.name}
                      </Link>
                    </>
                  )}
                </p>
              )}
              {team.sport && (
                <p className={`${theme.textSecondary} flex items-center mt-1`}>
                  <Trophy className="w-4 h-4 mr-1" />
                  {team.sport}
                </p>
              )}
            </div>
          </div>
        </div>

        {showEditButton && isOwner() && (
          <button
            onClick={handleEditClick}
            className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-x-2`}
          >
            <Edit className="w-4 h-4" />
            <span>Edit Team</span>
          </button>
        )}
      </div>

      {/* Team Info */}
      <div className={`${theme.card} rounded-xl ${theme.shadow} p-6 mb-8`}>
        <h2 className={`text-2xl font-bold ${theme.text} mb-4`}>About</h2>
        <p className={`${theme.textSecondary} mb-6`}>
          {team.description || 'No description available for this team.'}
        </p>

        <div className="grid md:grid-cols-3 gap-4">
          <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
            <div className={`text-2xl font-bold ${theme.text}`}>{maxMembers}</div>
            <div className={`text-sm ${theme.textMuted}`}>Max Members</div>
          </div>
          <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
            <div className={`text-2xl font-bold ${theme.text}`}>{players.length}</div>
            <div className={`text-sm ${theme.textMuted}`}>Current Players</div>
          </div>
          <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
            <div className={`text-2xl font-bold ${theme.text}`}>
              {team.createdAt ? new Date(team.createdAt).getFullYear() : '-'}
            </div>
            <div className={`text-sm ${theme.textMuted}`}>Founded</div>
          </div>
        </div>
      </div>

      {/* Team Stats */}
      {(team.wins !== undefined || team.losses !== undefined) && (
        <div className={`${theme.card} rounded-xl ${theme.shadow} p-6 mb-8`}>
          <h2 className={`text-2xl font-bold ${theme.text} mb-6 flex items-center`}>
            <Trophy className="w-6 h-6 mr-2 text-yellow-500" />
            Team Statistics
          </h2>

          <div className="grid md:grid-cols-4 gap-4">
            {team.ranking && (
              <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
                <div className={`text-2xl font-bold ${theme.text}`}>{team.ranking}</div>
                <div className={`text-sm ${theme.textMuted}`}>Ranking</div>
              </div>
            )}
            {team.wins !== undefined && (
              <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
                <div className={`text-2xl font-bold text-green-600`}>{team.wins}</div>
                <div className={`text-sm ${theme.textMuted}`}>Wins</div>
              </div>
            )}
            {team.losses !== undefined && (
              <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
                <div className={`text-2xl font-bold text-red-600`}>{team.losses}</div>
                <div className={`text-sm ${theme.textMuted}`}>Losses</div>
              </div>
            )}
            {team.wins !== undefined && team.losses !== undefined && (
              <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
                <div className={`text-2xl font-bold ${theme.text}`}>
                  {team.wins + team.losses > 0
                    ? ((team.wins / (team.wins + team.losses)) * 100).toFixed(1)
                    : 0}%
                </div>
                <div className={`text-sm ${theme.textMuted}`}>Win Rate</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Players List */}
      <div className={`${theme.card} rounded-xl ${theme.shadow} p-6`}>
        <h2 className={`text-2xl font-bold ${theme.text} mb-6 flex items-center`}>
          <Users className="w-6 h-6 mr-2 text-blue-500" />
          Player Roster
        </h2>

        {players.length > 0 ? (
          <div className="space-y-4">
            {players.map((player, index) => (
              <div
                key={player.id || index}
                className={`${theme.backgroundSecondary} rounded-lg p-4 ${theme.cardHover} cursor-pointer transition-colors`}
                onClick={() => handlePlayerClick(player)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-x-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white">
                      {(player.profileImage || player.profile_image || player.user?.profileImage || player.user?.profile_image) ? (
                        <img
                          src={resolveImageUrl(player.profileImage || player.profile_image || player.user?.profileImage || player.user?.profile_image)}
                          alt={player.name || player.user?.firstName || player.firstName}
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        <User className="w-6 h-6" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-x-2">
                        <h3 className={`text-lg font-semibold ${theme.text}`}>
                          {player.number && `#${player.number} `}
                          {player.name ||
                           (player.user ? `${player.user.firstName || player.user.first_name || ''} ${player.user.lastName || player.user.last_name || ''}`.trim() : '') ||
                           `${player.firstName || player.first_name || ''} ${player.lastName || player.last_name || ''}`.trim() ||
                           'Unknown Player'}
                        </h3>
                        {(player.isCaptain || player.is_captain) && (
                          <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full flex items-center">
                            <Award className="w-3 h-3 mr-1" />
                            Captain
                          </span>
                        )}
                      </div>
                      {player.position && (
                        <p className={`${theme.textSecondary} text-sm`}>
                          {player.position}
                        </p>
                      )}
                    </div>
                  </div>

                  {(player.points || player.assists || player.rebounds) && (
                    <div className="grid grid-cols-3 gap-6 text-center">
                      {player.points && (
                        <div>
                          <div className={`text-lg font-bold ${theme.text}`}>{player.points}</div>
                          <div className={`text-xs ${theme.textMuted}`}>PPG</div>
                        </div>
                      )}
                      {player.assists && (
                        <div>
                          <div className={`text-lg font-bold ${theme.text}`}>{player.assists}</div>
                          <div className={`text-xs ${theme.textMuted}`}>APG</div>
                        </div>
                      )}
                      {player.rebounds && (
                        <div>
                          <div className={`text-lg font-bold ${theme.text}`}>{player.rebounds}</div>
                          <div className={`text-xs ${theme.textMuted}`}>RPG</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Users className={`w-16 h-16 ${theme.textMuted} mx-auto mb-4`} />
            <h3 className={`text-lg font-medium ${theme.text} mb-2`}>No players found</h3>
            <p className={`${theme.textSecondary}`}>This team doesn't have any players yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamDetail;
